import React from 'react'
import styled from 'styled-components'
import BaseAsyncSelectInput from './base-async-select.component'
import BaseCheckboxToggleInput from './base-checkbox-toggle-input.component'
import BaseInput from './base-input.component'
import BaseMaskedInput from './base-masked-input.component'
import BaseSelectInput from './base-select-input.component'
import BaseTextAreaInput from './base-textarea-input.component'
import REGEX from '../../constants/regex.constants'
import _cardBrand from '../../constants/card-brand.constants'

const TextInput = ({
	id,
	blur,
	icon,
	type,
	label,
	style,
	errors,
	values,
	shadow,
	touched,
	onFocus,
	disabled,
	noBorder,
	noSpaces,
	position,
	attribute,
	className,
	minLength,
	maxLength,
	normalize,
	elementRef,
	handleBlur,
	information,
	onlyNumbers,
	placeholder,
	submitCount,
	handleChange,
	onlyAlphanumeric
}) => (
		<BaseInput
			id={id}
			icon={icon}
			label={label}
			style={style}
			blur={blur}
			shadow={shadow}
			onFocus={onFocus}
			error={errors[id]}
			value={values[id]}
			disabled={disabled}
			noBorder={noBorder}
			onBlur={handleBlur}
			position={position}
			attribute={attribute}
			className={className}
			maxLength={maxLength}
			minLength={minLength}
			touched={touched[id]}
			type={type || 'text'}
			elementRef={elementRef}
			information={information}
			placeholder={placeholder}
			submitCount={submitCount}
			onChange={e => {
				if (normalize) {
					e.target.value = e.target.value
						.$replaceAccentsAndCedilha()
						.replace(REGEX.NUMBERS_AND_SPACES, '')
				}

				if (onlyNumbers) {
					e.target.value = e.target.value.replace(/[^0-9]+/g, '')
				}

				if (noSpaces) {
					e.target.value = e.target.value.replace(/\s/g, '')
				}

				if (onlyAlphanumeric) {
					e.target.value = e.target.value.replace(/[^A-Z0-9]/gi, '')
				}
				handleChange(e)
			}}
		/>
	)

const EmailInput = props => <TextInput type="email" {...props} />
const PasswordInput = props => <TextInput type="password" {...props} />

/**
 * Not a input type password, it hides the password, but browser won't ask to save it.
 */
const FakePasswordInput = props => <TextInput type="text" {...props} />

const StyledFakePasswordInput = styled(FakePasswordInput)`
	input {
		font-family: text-security-disc;
	}
`

const MaskedInput = ({
	id,
	mask,
	icon,
	type,
	label,
	style,
	values,
	errors,
	shadow,
	touched,
	disabled,
	dispatch,
	noBorder,
	className,
	maxLength,
	elementRef,
	onComplete,
	placeholder,
	information,
	submitCount,
	setFieldValue,
	setFieldTouched,
	setFieldValueHandler,
	toClean
}) => (
		<BaseMaskedInput
			id={id}
			label={label}
			icon={icon}
			mask={mask}
			style={style}
			shadow={shadow}
			error={errors[id]}
			value={values[id]}
			toClean={toClean}
			noBorder={noBorder}
			disabled={disabled}
			dispatch={dispatch}
			type={type || 'text'}
			className={className}
			touched={touched[id]}
			maxLength={maxLength}
			elementRef={elementRef}
			onComplete={onComplete}
			onBlur={setFieldTouched}
			onChange={setFieldValue}
			onChangeHandler={setFieldValueHandler}
			information={information}
			placeholder={placeholder}
			submitCount={submitCount}
		/>
	)

const NumberInput = props => (
	<MaskedInput type="tel" mask={REGEX.ONLYDIGITS} {...props} />
)

const MoneyInput = props => (
	<MaskedInput mask={REGEX.CURRENCY} maxLength={16} type={'tel'} {...props} />
)

const SecurityCodeInput = props => (
	<MaskedInput type="tel" mask="0000" {...props} />
)

const CPFInput = props => (
	<MaskedInput type="tel" mask="000.000.000-00" {...props} />
)
const CNPJInput = props => (
	<MaskedInput type="tel" mask="00.000.000/0000-00" {...props} />
)
const StateRegistrationInput = props => (
	<MaskedInput type="tel" mask="0000000000" {...props} />
)
const RGInput = props => <MaskedInput type="tel" mask={REGEX.RG} {...props} />

const DateInput = props => (
	<MaskedInput type="tel" mask="00/00/0000" {...props} />
)
const CEPInput = props => <MaskedInput type="tel" mask="00000-000" {...props} />
const PhoneInput = props => (
	<MaskedInput
		type="tel"
		mask={[{ mask: '(00) 00000 0000' }, { mask: '(00) 0000 0000' }]}
		dispatch={function (appended, dynamicMasked) {
			var number = (dynamicMasked.value + appended).replace(/\D/g, '')

			return number.length > 10
				? dynamicMasked.compiledMasks[0]
				: dynamicMasked.compiledMasks[1]
		}}
		{...props}
	/>
)
const LandlineInput = props => (
	<MaskedInput type="tel" mask="(00) 0000 0000" {...props} />
)
const CelPhoneInput = props => (
	<MaskedInput type="tel" mask="(00) 00000 0000" {...props} />
)

const CreditCardInput = props => {
	return <MaskedInput {...props} type="tel" mask="0000 0000 0000 0000" />
}

const DebitCardInput = props => {
	return <MaskedInput {...props} type="tel" mask="0000 0000 0000 0000" />
}

const MuvicardInput = props => {
	return <MaskedInput {...props} type="tel" mask="000.000.000-00" />
}

const AmexCreditCardInput = props => {
	return (
		<MaskedInput
			{...props}
			type="tel"
			mask="0000 000000 00000"
			maxLength={17}
		/>
	)
}

const CouponInput = props => (
	<MaskedInput
		mask={REGEX.COUPON}
		{...props}
		style={{ textTransform: 'uppercase' }}
	/>
)

const SelectInput = ({
	id,
	type,
	label,
	values,
	errors,
	options,
	touched,
	disabled,
	position,
	attribute,
	className,
	elementRef,
	isClearable,
	placeholder,
	submitCount,
	defaultValue,
	onValueChange,
	setFieldValue,
	setFieldTouched,
	noOptionsMessage
}) => (
		<BaseSelectInput
			id={id}
			label={label}
			options={options}
			error={errors[id]}
			value={values[id]}
			isClearable={isClearable}
			disabled={disabled}
			position={position}
			attribute={attribute}
			className={className}
			touched={touched[id]}
			type={type || 'text'}
			elementRef={elementRef}
			onBlur={setFieldTouched}
			onChange={setFieldValue}
			placeholder={placeholder}
			submitCount={submitCount}
			defaultValue={defaultValue}
			onValueChange={onValueChange}
			noOptionsMessage={noOptionsMessage}
		/>
	)

const AsyncSelectInput = ({
	id,
	label,
	errors,
	values,
	options,
	touched,
	disabled,
	className,
	elementRef,
	loadOptions,
	placeholder,
	submitCount,
	defaultValue,
	onValueChange,
	setFieldValue,
	defaultOptions,
	setFieldTouched
}) => (
		<BaseAsyncSelectInput
			id={id}
			label={label}
			options={options}
			error={errors[id]}
			value={values[id]}
			disabled={disabled}
			className={className}
			touched={touched[id]}
			elementRef={elementRef}
			onBlur={setFieldTouched}
			onChange={setFieldValue}
			loadOptions={loadOptions}
			placeholder={placeholder}
			submitCount={submitCount}
			defaultValue={defaultValue}
			onValueChange={onValueChange}
			defaultOptions={defaultOptions}
		/>
	)

const CheckboxToggleInput = ({
	elementRef,
	id,
	label,
	className,
	disabled,
	values,
	errors,
	setFieldValue,
	handleBlur,
	touched,
	submitCount,
	onValueChange
}) => (
		<BaseCheckboxToggleInput
			id={id}
			label={label}
			value={values[id]}
			error={errors[id]}
			onBlur={handleBlur}
			disabled={disabled}
			className={className}
			touched={touched[id]}
			elementRef={elementRef}
			onChange={setFieldValue}
			submitCount={submitCount}
			onValueChange={onValueChange}
		/>
	)

const TextAreaInput = ({
	elementRef,
	id,
	label,
	className,
	placeholder,
	maxLength,
	disabled,
	values,
	errors,
	handleChange,
	handleBlur,
	touched,
	submitCount,
	shadow,
	noBorder,
	rows
}) => (
		<BaseTextAreaInput
			elementRef={elementRef}
			id={id}
			label={label}
			className={className}
			placeholder={placeholder}
			maxLength={maxLength}
			disabled={disabled}
			value={values[id]}
			error={errors[id]}
			onChange={handleChange}
			onBlur={handleBlur}
			touched={touched[id]}
			submitCount={submitCount}
			shadow={shadow}
			noBorder={noBorder}
			rows={rows}
		/>
	)

export {
	AsyncSelectInput,
	CelPhoneInput,
	CEPInput,
	CheckboxToggleInput,
	CPFInput,
	CNPJInput,
	StateRegistrationInput,
	RGInput,
	AmexCreditCardInput,
	CreditCardInput,
	DebitCardInput,
	MuvicardInput,
	DateInput,
	EmailInput,
	LandlineInput,
	NumberInput,
	PasswordInput,
	PhoneInput,
	SelectInput,
	TextAreaInput,
	TextInput,
	SecurityCodeInput,
	CouponInput,
	MoneyInput,
	StyledFakePasswordInput as FakePasswordInput
}
